import type { Priority } from '@/types';
import { isCompletedStatus, normalizeStatus, TASK_STATUS, DEFAULT_TASK_STATUS } from '@/constants/taskStatuses';

export type { Priority };
import { supabase } from '@/lib/supabaseClient';
import { withRetry } from '@/utils/retry';


// Helper to convert Date to timestamp (number)
function dateToTimestamp(date: Date | null | undefined): number | null {
  if (!date) return null;
  return date.getTime();
}

// Helper to convert Date values in customFields to timestamps
function convertCustomFieldDates(
  fields: Record<string, unknown> | null | undefined
): Record<string, unknown> | null {
  if (!fields || Object.keys(fields).length === 0) return null;
  const converted: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(fields)) {
    converted[key] = value instanceof Date ? value.getTime() : value;
  }
  return converted;
}

// Helper to convert Date values in estimateHistory to timestamps
function convertEstimateHistory(
  history: Task['estimateHistory'] | null | undefined
): TaskDoc['estimate_history'] | null {
  if (!history) return null;
  return {
    initialTotal: history.initialTotal,
    initialEstimatesByAssignee: history.initialEstimatesByAssignee,
    initialTimestamp: history.initialTimestamp
      ? (typeof history.initialTimestamp === 'number'
        ? history.initialTimestamp
        : (dateToTimestamp(history.initialTimestamp && typeof history.initialTimestamp === 'object' && 'getTime' in history.initialTimestamp ? history.initialTimestamp as Date : null) ?? undefined))
      : undefined,
    entries: (history.entries || []).map(entry => ({
      ...entry,
      timestamp: typeof entry.timestamp === 'number'
        ? entry.timestamp
        : (dateToTimestamp(entry.timestamp && typeof entry.timestamp === 'object' && 'getTime' in entry.timestamp ? entry.timestamp as Date : null) || Date.now()),
    })),
  };
}

// Supabase task document (as stored in Supabase)
export type TaskDoc = {
  id: string; // UUID
  created_at: string; // ISO timestamp
  name: string;
  content: string | null;
  status: string;
  date_created: number | null; // timestamp in milliseconds
  due_date?: number | null; // timestamp in milliseconds
  start_date?: number | null; // timestamp in milliseconds
  parent_id?: string | null; // UUID
  space_id: string; // UUID
  sprint_id?: string | null; // UUID
  epic_id?: string | null; // UUID
  story_points?: number | null;
  attachments: string[] | null;
  assignees: string[] | null;
  tags: string[] | null;
  priority?: Priority | null;
  time_estimated?: number | null;
  time_estimates_by_assignee?: Record<string, number> | null;
  rolled_up_time?: number | null;
  checklists?: Record<string, unknown> | null;
  comments?: unknown[] | null;
  assigned_comments: number | null;
  custom_fields?: Record<string, unknown> | null;
  milestone_id?: string | null; // UUID
  template_id?: string | null; // UUID
  estimate_history?: {
    initialTotal?: number;
    initialEstimatesByAssignee?: Record<string, number>;
    initialTimestamp?: number;
    entries?: Array<{
      id: string;
      timestamp: number;
      type: 'total' | 'assignee';
      assignee?: string;
      change: number;
      previousTotal?: number;
      newTotal?: number;
      previousAssigneeEstimate?: number;
      newAssigneeEstimate?: number;
      reason?: string;
    }>;
  } | null;
  baseline_start_date?: number | null; // timestamp in milliseconds
  baseline_due_date?: number | null;   // timestamp in milliseconds
  scheduled_start?: number | null; // timestamp in milliseconds
  scheduled_end?: number | null;   // timestamp in milliseconds
  sort_order?: number | null;
  updated_at: string; // ISO timestamp
};

// UI-friendly task type used across the app.
// This is a clean camelCase interface - NOT extending TaskDoc.
// Conversion from Supabase (snake_case) happens in convertTaskFromSupabase().
export interface Task {
  // Core fields
  id: string;
  name: string;
  content: string;
  status: string;

  // Timestamps (as milliseconds)
  dateCreated: number;
  dueDate: number | null;
  startDate: number | null;

  // Relationships
  parentId: string | null;
  spaceId: string;
  sprintId: string | null;
  epicId: string | null;
  milestoneId: string | null;
  templateId: string | null;

  // Planning
  storyPoints: number | null;

  // Arrays
  attachments: string[];
  assignees: string[];
  tags: string[];

  // Optional fields
  priority: Priority | null;
  timeEstimated: number | null;
  timeEstimatesByAssignee: Record<string, number> | null;
  rolledUpTime: number | null;
  checklists: Record<string, unknown>;
  comments: unknown[];
  assignedComments: number;
  customFields: Record<string, unknown> | null;
  estimateHistory: {
    initialTotal?: number;
    initialEstimatesByAssignee?: Record<string, number>;
    initialTimestamp?: number;
    entries?: Array<{
      id: string;
      timestamp: number;
      type: 'total' | 'assignee';
      assignee?: string;
      change: number;
      previousTotal?: number;
      newTotal?: number;
      previousAssigneeEstimate?: number;
      newAssigneeEstimate?: number;
      reason?: string;
    }>;
  } | null;

  // Baseline dates for Gantt comparison
  baselineStartDate: number | null;
  baselineDueDate: number | null;

  // Scheduling (time-slot calendar)
  scheduledStart: number | null;
  scheduledEnd: number | null;

  // Drag-and-drop ordering within a parent
  sortOrder: number | null;

  // Metadata from Supabase
  createdAt: string;
  updatedAt: string;

  // Derived fields from containersMap (added by useTaskEnrichment)
  path?: string;
  spaceName?: string;
  folderPath?: string[];
  listName?: string;
}

// Helper to convert Supabase task (snake_case) to UI format (camelCase)
export function convertTaskFromSupabase(row: TaskDoc): Task {
  return {
    // Core fields
    id: row.id,
    name: row.name,
    content: row.content ?? '',
    status: normalizeStatus(row.status),

    // Timestamps
    dateCreated: row.date_created ?? Date.now(),
    dueDate: row.due_date ?? null,
    startDate: row.start_date ?? null,

    // Relationships
    parentId: row.parent_id ?? null,
    spaceId: row.space_id,
    sprintId: row.sprint_id ?? null,
    epicId: row.epic_id ?? null,
    milestoneId: row.milestone_id ?? null,
    templateId: row.template_id ?? null,

    // Planning
    storyPoints: row.story_points ?? null,

    // Arrays
    attachments: row.attachments || [],
    assignees: row.assignees || [],
    tags: row.tags || [],

    // Optional fields
    priority: row.priority ?? null,
    timeEstimated: row.time_estimated ?? null,
    timeEstimatesByAssignee: row.time_estimates_by_assignee ?? null,
    rolledUpTime: row.rolled_up_time ?? null,
    checklists: row.checklists || {},
    comments: row.comments || [],
    assignedComments: row.assigned_comments ?? 0,
    customFields: row.custom_fields ?? null,
    estimateHistory: row.estimate_history ?? null,

    // Baseline dates
    baselineStartDate: row.baseline_start_date ?? null,
    baselineDueDate: row.baseline_due_date ?? null,

    // Scheduling
    scheduledStart: row.scheduled_start ?? null,
    scheduledEnd: row.scheduled_end ?? null,

    // Ordering
    sortOrder: row.sort_order ?? null,

    // Metadata
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export async function fetchAllTasksPaginated(
  onProgress?: (progress: { current: number; total: number }) => void
): Promise<Task[]> {
  const BATCH_SIZE = 1000;
  const allTasks: Task[] = [];
  let offset = 0;
  let pageNum = 0;
  let hasMore = true;

  // Count query for progress tracking
  const { count } = await supabase
    .from('tasks')
    .select('*', { count: 'exact', head: true });

  const totalTasks = count || 0;
  const totalPages = Math.ceil(totalTasks / BATCH_SIZE);

  while (hasMore) {
    const { data, error } = await withRetry(() =>
      supabase
        .from('tasks')
        .select('*')
        .order('created_at', { ascending: false })
        .range(offset, offset + BATCH_SIZE - 1)
    );

    if (error) throw error;

    pageNum += 1;

    if (!data || data.length === 0) {
      hasMore = false;
    } else {
      allTasks.push(...data.map(convertTaskFromSupabase));

      if (onProgress) {
        onProgress({ current: pageNum, total: totalPages });
      }

      offset += BATCH_SIZE;
      if (data.length < BATCH_SIZE) {
        hasMore = false;
      }
    }
  }

  return allTasks;
}

export async function createTask(taskData: Partial<Omit<Task, 'id'>>): Promise<Task & { id: string }> {
  // Ensure spaceId is provided (App.handleCreateTask resolves it from names)
  if (!taskData.spaceId) {
    throw new Error('spaceId is required to create a task');
  }

  const insertData = {
    name: taskData.name ?? 'New task',
    content: taskData.content ?? '',
    status: normalizeStatus(taskData.status) ?? DEFAULT_TASK_STATUS,
    date_created: typeof taskData.dateCreated === 'number' ? taskData.dateCreated : (dateToTimestamp(taskData.dateCreated && typeof taskData.dateCreated === 'object' && 'getTime' in taskData.dateCreated ? taskData.dateCreated as Date : null) ?? Date.now()),
    due_date: typeof taskData.dueDate === 'number' ? taskData.dueDate : dateToTimestamp(taskData.dueDate && typeof taskData.dueDate === 'object' && 'getTime' in taskData.dueDate ? taskData.dueDate as Date : null),
    start_date: typeof taskData.startDate === 'number' ? taskData.startDate : dateToTimestamp(taskData.startDate && typeof taskData.startDate === 'object' && 'getTime' in taskData.startDate ? taskData.startDate as Date : null),
    parent_id: taskData.parentId || null,
    space_id: taskData.spaceId,
    sprint_id: taskData.sprintId || null,
    epic_id: taskData.epicId || null,
    milestone_id: taskData.milestoneId || null,
    template_id: taskData.templateId || null,
    story_points: taskData.storyPoints ?? null,
    attachments: taskData.attachments ?? [],
    assignees: taskData.assignees ?? [],
    tags: taskData.tags ?? [],
    priority: (taskData.priority === 'urgent' || taskData.priority === 'high' || taskData.priority === 'normal' || taskData.priority === 'low' ? taskData.priority : null),
    time_estimated: taskData.timeEstimated ?? null,
    time_estimates_by_assignee: taskData.timeEstimatesByAssignee ?? null,
    rolled_up_time: taskData.rolledUpTime ?? null,
    checklists: taskData.checklists ?? {},
    comments: taskData.comments ?? [],
    assigned_comments: taskData.assignedComments ?? 0,
    custom_fields: convertCustomFieldDates(taskData.customFields),
    estimate_history: convertEstimateHistory(taskData.estimateHistory),
    scheduled_start: typeof taskData.scheduledStart === 'number' ? taskData.scheduledStart : null,
    scheduled_end: typeof taskData.scheduledEnd === 'number' ? taskData.scheduledEnd : null,
  };

  const { data, error } = await withRetry(() =>
    supabase
      .from('tasks')
      .insert(insertData)
      .select()
      .single()
  );

  if (error || !data) {
    throw new Error(error?.message || 'Failed to create task');
  }

  // Log task event for delta sync (non-critical, don't block on failure)
  await withRetry(() => supabase.from('task_events').insert({
    ts: Date.now(),
    type: 'upsert',
    task_id: data.id,
    task: data,
    space_id: data.space_id,
  })).then(null, () => {});

  return convertTaskFromSupabase(data) as Task;
}

export async function updateTask(id: string, updates: Partial<Omit<Task, 'id'>>): Promise<Task | null> {
  const updatePayload: Record<string, unknown> = {};
  if (updates.name !== undefined) updatePayload.name = updates.name;
  if (updates.content !== undefined) updatePayload.content = updates.content;
  if (updates.status !== undefined) updatePayload.status = normalizeStatus(updates.status);
  if (updates.dateCreated !== undefined) {
    const dateCreatedValue = typeof updates.dateCreated === 'number' ? updates.dateCreated : dateToTimestamp(updates.dateCreated && typeof updates.dateCreated === 'object' && 'getTime' in updates.dateCreated ? updates.dateCreated as Date : null);
    updatePayload.date_created = dateCreatedValue ?? undefined;
  }
  if (updates.dueDate !== undefined) {
    const dueDateValue = typeof updates.dueDate === 'number' ? updates.dueDate : dateToTimestamp(updates.dueDate && typeof updates.dueDate === 'object' && 'getTime' in updates.dueDate ? updates.dueDate as Date : null);
    updatePayload.due_date = dueDateValue ?? undefined;
  }
  if (updates.startDate !== undefined) {
    const startDateValue = typeof updates.startDate === 'number' ? updates.startDate : dateToTimestamp(updates.startDate && typeof updates.startDate === 'object' && 'getTime' in updates.startDate ? updates.startDate as Date : null);
    updatePayload.start_date = startDateValue ?? undefined;
  }
  if (updates.parentId !== undefined) updatePayload.parent_id = updates.parentId || null;
  if (updates.spaceId !== undefined) updatePayload.space_id = updates.spaceId;
  if (updates.sprintId !== undefined) updatePayload.sprint_id = updates.sprintId || null;
  if (updates.epicId !== undefined) updatePayload.epic_id = updates.epicId || null;
  if (updates.milestoneId !== undefined) updatePayload.milestone_id = updates.milestoneId || null;
  if (updates.templateId !== undefined) updatePayload.template_id = updates.templateId || null;
  if (updates.storyPoints !== undefined) updatePayload.story_points = updates.storyPoints;
  if (updates.attachments !== undefined) updatePayload.attachments = updates.attachments;
  if (updates.assignees !== undefined) updatePayload.assignees = updates.assignees;
  if (updates.tags !== undefined) updatePayload.tags = updates.tags;
  if (updates.priority !== undefined) {
    updatePayload.priority = (updates.priority === 'urgent' || updates.priority === 'high' || updates.priority === 'normal' || updates.priority === 'low' ? updates.priority : null);
  }
  if (updates.timeEstimated !== undefined) updatePayload.time_estimated = updates.timeEstimated;
  if (updates.timeEstimatesByAssignee !== undefined) updatePayload.time_estimates_by_assignee = updates.timeEstimatesByAssignee;
  if (updates.rolledUpTime !== undefined) updatePayload.rolled_up_time = updates.rolledUpTime;
  if (updates.checklists !== undefined) updatePayload.checklists = updates.checklists;
  if (updates.comments !== undefined) updatePayload.comments = updates.comments;
  if (updates.assignedComments !== undefined) updatePayload.assigned_comments = updates.assignedComments;
  if (updates.customFields !== undefined) updatePayload.custom_fields = convertCustomFieldDates(updates.customFields);
  if (updates.estimateHistory !== undefined) updatePayload.estimate_history = convertEstimateHistory(updates.estimateHistory);
  if (updates.baselineStartDate !== undefined) updatePayload.baseline_start_date = typeof updates.baselineStartDate === 'number' ? updates.baselineStartDate : null;
  if (updates.baselineDueDate !== undefined) updatePayload.baseline_due_date = typeof updates.baselineDueDate === 'number' ? updates.baselineDueDate : null;
  if (updates.scheduledStart !== undefined) updatePayload.scheduled_start = typeof updates.scheduledStart === 'number' ? updates.scheduledStart : null;
  if (updates.scheduledEnd !== undefined) updatePayload.scheduled_end = typeof updates.scheduledEnd === 'number' ? updates.scheduledEnd : null;
  if (updates.sortOrder !== undefined) updatePayload.sort_order = updates.sortOrder;

  const { data, error } = await withRetry(() =>
    supabase
      .from('tasks')
      .update(updatePayload)
      .eq('id', id)
      .select()
      .single()
  );

  if (error || !data) {
    throw error || new Error('Failed to update task');
  }

  // Log task event for delta sync (non-critical)
  await withRetry(() => supabase.from('task_events').insert({
    ts: Date.now(),
    type: 'upsert',
    task_id: data.id,
    task: data,
    space_id: data.space_id,
  })).then(null, () => {});

  return convertTaskFromSupabase(data);
}

export async function deleteTask(id: string): Promise<boolean> {
  // Get task before deletion for event log
  const { data: task } = await withRetry(() =>
    supabase.from('tasks').select('*').eq('id', id).single()
  );

  const { error } = await withRetry(() =>
    supabase.from('tasks').delete().eq('id', id)
  );

  if (error) {
    throw error;
  }

  // Log task event for delta sync (non-critical)
  if (task) {
    await withRetry(() => supabase.from('task_events').insert({
      ts: Date.now(),
      type: 'delete',
      task_id: id,
      space_id: task.space_id,
    })).then(null, () => {});
  }

  return true;
}

export async function bulkUpdateTasks(ids: string[], updates: Partial<Omit<Task, 'id'>>): Promise<Task[]> {
  const updatePayload: Record<string, unknown> = {};
  if (updates.name !== undefined) updatePayload.name = updates.name;
  if (updates.content !== undefined) updatePayload.content = updates.content;
  if (updates.status !== undefined) updatePayload.status = normalizeStatus(updates.status);
  if (updates.dateCreated !== undefined) updatePayload.date_created = updates.dateCreated;
  if (updates.dueDate !== undefined) {
    updatePayload.due_date = typeof updates.dueDate === 'number' ? updates.dueDate : null;
  }
  if (updates.startDate !== undefined) {
    updatePayload.start_date = typeof updates.startDate === 'number' ? updates.startDate : null;
  }
  if (updates.parentId !== undefined) updatePayload.parent_id = updates.parentId || null;
  if (updates.spaceId !== undefined) updatePayload.space_id = updates.spaceId;
  if (updates.sprintId !== undefined) updatePayload.sprint_id = updates.sprintId || null;
  if (updates.epicId !== undefined) updatePayload.epic_id = updates.epicId || null;
  if (updates.milestoneId !== undefined) updatePayload.milestone_id = updates.milestoneId || null;
  if (updates.templateId !== undefined) updatePayload.template_id = updates.templateId || null;
  if (updates.storyPoints !== undefined) updatePayload.story_points = updates.storyPoints;
  if (updates.attachments !== undefined) updatePayload.attachments = updates.attachments;
  if (updates.assignees !== undefined) updatePayload.assignees = updates.assignees;
  if (updates.tags !== undefined) updatePayload.tags = updates.tags;
  if (updates.priority !== undefined) {
    updatePayload.priority = (updates.priority === 'urgent' || updates.priority === 'high' || updates.priority === 'normal' || updates.priority === 'low' ? updates.priority : null);
  }
  if (updates.timeEstimated !== undefined) updatePayload.time_estimated = updates.timeEstimated;
  if (updates.timeEstimatesByAssignee !== undefined) updatePayload.time_estimates_by_assignee = updates.timeEstimatesByAssignee;
  if (updates.rolledUpTime !== undefined) updatePayload.rolled_up_time = updates.rolledUpTime;
  if (updates.checklists !== undefined) updatePayload.checklists = updates.checklists;
  if (updates.comments !== undefined) updatePayload.comments = updates.comments;
  if (updates.assignedComments !== undefined) updatePayload.assigned_comments = updates.assignedComments;
  if (updates.customFields !== undefined) updatePayload.custom_fields = convertCustomFieldDates(updates.customFields);
  if (updates.estimateHistory !== undefined) updatePayload.estimate_history = convertEstimateHistory(updates.estimateHistory);
  if (updates.baselineStartDate !== undefined) updatePayload.baseline_start_date = typeof updates.baselineStartDate === 'number' ? updates.baselineStartDate : null;
  if (updates.baselineDueDate !== undefined) updatePayload.baseline_due_date = typeof updates.baselineDueDate === 'number' ? updates.baselineDueDate : null;
  if (updates.scheduledStart !== undefined) updatePayload.scheduled_start = typeof updates.scheduledStart === 'number' ? updates.scheduledStart : null;
  if (updates.scheduledEnd !== undefined) updatePayload.scheduled_end = typeof updates.scheduledEnd === 'number' ? updates.scheduledEnd : null;

  const { data, error } = await withRetry(() =>
    supabase
      .from('tasks')
      .update(updatePayload)
      .in('id', ids)
      .select()
  );

  if (error) {
    throw error;
  }

  // Log task events for delta sync (non-critical)
  if (data) {
    const events = data.map(task => ({
      ts: Date.now(),
      type: 'upsert' as const,
      task_id: task.id,
      task: task,
      space_id: task.space_id,
    }));
    await withRetry(() => supabase.from('task_events').insert(events)).then(null, () => {});
  }

  return (data || []).map(convertTaskFromSupabase);
}

export async function bulkDeleteTasks(ids: string[]): Promise<number> {
  // Get tasks before deletion for event log
  const { data: tasks } = await withRetry(() =>
    supabase.from('tasks').select('id, space_id').in('id', ids)
  );

  const { error } = await withRetry(() =>
    supabase.from('tasks').delete().in('id', ids)
  );

  if (error) {
    throw error;
  }

  // Log task events for delta sync (non-critical)
  if (tasks) {
    const events = tasks.map(task => ({
      ts: Date.now(),
      type: 'delete' as const,
      task_id: task.id,
      space_id: task.space_id,
    }));
    await withRetry(() => supabase.from('task_events').insert(events)).then(null, () => {});
  }

  return ids.length;
}

export async function bulkDuplicateTasks(ids: string[]): Promise<Task[]> {
  // Use atomic RPC for bulk duplicate (handles event logging in transaction)
  const { data, error } = await withRetry(() =>
    supabase.rpc('bulk_duplicate_tasks', { p_task_ids: ids })
  );

  if (error) {
    throw error;
  }

  if (!data || data.length === 0) {
    return [];
  }

  return data.map(convertTaskFromSupabase);
}

export async function restoreTask(task: Task): Promise<void> {
  const insertData = {
    id: task.id,
    name: task.name,
    content: task.content,
    status: task.status,
    date_created: task.dateCreated,
    due_date: task.dueDate,
    start_date: task.startDate,
    parent_id: task.parentId,
    space_id: task.spaceId,
    sprint_id: task.sprintId,
    epic_id: task.epicId,
    milestone_id: task.milestoneId,
    template_id: task.templateId,
    story_points: task.storyPoints,
    attachments: task.attachments,
    assignees: task.assignees,
    tags: task.tags,
    priority: task.priority,
    time_estimated: task.timeEstimated,
    time_estimates_by_assignee: task.timeEstimatesByAssignee,
    rolled_up_time: task.rolledUpTime,
    checklists: task.checklists,
    comments: task.comments,
    assigned_comments: task.assignedComments,
    custom_fields: task.customFields,
    estimate_history: task.estimateHistory,
    scheduled_start: task.scheduledStart,
    scheduled_end: task.scheduledEnd,
  };

  const { error } = await withRetry(() =>
    supabase.from('tasks').insert(insertData).select().single()
  );

  if (error) {
    throw new Error(`Failed to restore task: ${error.message}`);
  }

  // Log task event for delta sync (non-critical)
  await withRetry(() => supabase.from('task_events').insert({
    ts: Date.now(),
    type: 'upsert',
    task_id: task.id,
    task: insertData,
    space_id: task.spaceId,
  })).then(null, () => {});
}

export function getStats(tasks: Array<Task & { id: string }>) {
  const totalTasks = tasks.length;
  const completedTasks = tasks.filter((t) => isCompletedStatus(t.status)).length;
  const inProgressTasks = tasks.filter((t) => normalizeStatus(t.status) === TASK_STATUS.IN_PROGRESS).length;
  const blockedTasks = tasks.filter((t) => normalizeStatus(t.status) === TASK_STATUS.BLOCKED).length;
  const openTasks = tasks.filter((t) => {
    const status = normalizeStatus(t.status);
    return status === TASK_STATUS.OPEN || status === TASK_STATUS.TODO;
  }).length;
  const totalTimeEstimated = tasks.reduce((sum, t) => sum + (t.timeEstimated || 0), 0);

  return {
    totalTasks,
    completedTasks,
    inProgressTasks,
    blockedTasks,
    openTasks,
    totalTimeEstimated,
  };
}
